import { useMemo, useState } from 'react';
import { ClipboardList, FileText, FlaskConical, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatValue } from '@/lib/format';
import { assignmentStatusLabel } from '@/lib/psyWorkspace';
import { resultSummary } from '@/components/psy/PsyResultsTable';
import {
    filterByRange,
    hasRange,
    rangeInvalid,
    type DateRange,
    type PsyHistory,
} from '@/components/medic/patientCardData';
import { DateRangeFilter } from '@/components/medic/PatientCardDateRange';
import { InlineNotice, SectionState } from '@/components/medic/PatientCardStates';
import {
    PatientCardPsyDetailDialog,
    type PsyDetailEntry,
} from '@/components/medic/PatientCardPsyDetailDialog';

interface Props {
    data: PsyHistory;
    range: DateRange;
    onRangeChange: (next: DateRange) => void;
    onReload: () => void;
    onOpenTests?: (patientId: string) => void;
    patientId: string;
}

function shortText(value: string, limit = 120): string {
    const text = value.trim();
    if (text.length <= limit) return text;
    return `${text.slice(0, limit)}…`;
}

export function PatientCardPsySection({ data, range, onRangeChange, onReload, onOpenTests, patientId }: Props) {
    const [entry, setEntry] = useState<PsyDetailEntry | null>(null);

    const methodsByCode = useMemo(() => {
        const map = new Map<string, string>();
        for (const method of data.methods) map.set(method.code, method.synonym);
        return map;
    }, [data.methods]);

    const results = useMemo(
        () => filterByRange(data.results, (item) => item.createdAt, range),
        [data.results, range],
    );
    const assignments = useMemo(
        () => filterByRange(data.assignments, (item) => item.assignedDate, range),
        [data.assignments, range],
    );
    const conclusions = useMemo(
        () => filterByRange(data.conclusions, (item) => item.date, range),
        [data.conclusions, range],
    );

    if (data.state === 'unavailable') {
        return (
            <SectionState
                title="Психологические данные не ведутся"
                text={data.note || 'В этой базе нет тестов и заключений психолога.'}
            />
        );
    }
    if (data.state === 'denied') {
        return <SectionState title="Психологические данные недоступны" text={data.note} tone="warning" />;
    }
    if (data.state === 'error') {
        return <SectionState title="Не удалось загрузить психологические данные" text={data.note} tone="danger" onRetry={onReload} />;
    }

    const hasAny = data.results.length > 0 || data.assignments.length > 0 || data.conclusions.length > 0;
    const filteredEmpty = !rangeInvalid(range) && results.length === 0 && assignments.length === 0 && conclusions.length === 0;

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                <div className="rounded-lg border bg-card px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Результатов</div>
                    <div className="text-lg font-semibold">{data.results.length}</div>
                </div>
                <div className="rounded-lg border bg-card px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Назначений</div>
                    <div className="text-lg font-semibold">{data.assignments.length}</div>
                </div>
                <div className="rounded-lg border bg-card px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Заключений</div>
                    <div className="text-lg font-semibold">{data.conclusions.length}</div>
                </div>
                <div className="flex items-center justify-end rounded-lg border bg-card px-3 py-2">
                    {onOpenTests && (
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-[11px]"
                            onClick={() => onOpenTests(patientId)}
                        >
                            <FlaskConical className="mr-1 h-3 w-3" />
                            Все тесты
                        </Button>
                    )}
                </div>
            </div>

            {data.note && <InlineNotice text={data.note} tone="warning" onRetry={onReload} />}
            {data.resultsState !== 'ready' && (
                <InlineNotice
                    text={data.resultsError || (data.resultsState === 'denied'
                        ? 'Нет прав на чтение результатов тестов'
                        : 'Результаты тестов недоступны')}
                    tone={data.resultsState === 'error' ? 'danger' : 'warning'}
                    onRetry={onReload}
                />
            )}
            {data.assignmentsState !== 'ready' && (
                <InlineNotice
                    text={data.assignmentsError || (data.assignmentsState === 'denied'
                        ? 'Нет прав на чтение назначений'
                        : 'Назначения недоступны')}
                    tone={data.assignmentsState === 'error' ? 'danger' : 'warning'}
                    onRetry={onReload}
                />
            )}
            {data.conclusionsState !== 'ready' && (
                <InlineNotice
                    text={data.conclusionsError || (data.conclusionsState === 'denied'
                        ? 'Нет прав на чтение заключений'
                        : 'Заключения недоступны')}
                    tone={data.conclusionsState === 'error' ? 'danger' : 'warning'}
                    onRetry={onReload}
                />
            )}

            <DateRangeFilter value={range} onChange={onRangeChange} />

            {rangeInvalid(range) && (
                <SectionState
                    title="Период задан неверно"
                    text="Дата «с» позже даты «по». Исправьте период или сбросьте фильтр."
                    tone="warning"
                />
            )}

            {!rangeInvalid(range) && !hasAny && (
                <div className="rounded-lg border bg-card px-3 py-4 text-sm text-muted-foreground">
                    <div className="flex items-start gap-2">
                        <Sparkles className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>
                            Психологических тестов, назначений и заключений у пациента нет.
                        </span>
                    </div>
                </div>
            )}

            {!rangeInvalid(range) && hasAny && filteredEmpty && (
                <div className="rounded-lg border bg-card px-3 py-4 text-sm text-muted-foreground">
                    <div className="flex flex-wrap items-center gap-2">
                        <span>За выбранный период психологических данных нет.</span>
                        {hasRange(range) && (
                            <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-[11px]"
                                onClick={() => onRangeChange({ from: '', to: '' })}
                            >
                                Сбросить период
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {!rangeInvalid(range) && hasAny && !filteredEmpty && (
                <>
                    <section className="space-y-2">
                        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
                            <FlaskConical className="h-3.5 w-3.5" />
                            Результаты тестов
                        </div>
                        {results.length === 0 ? (
                            <div className="rounded-lg border px-3 py-2 text-xs text-muted-foreground">
                                {data.results.length === 0 ? 'Результатов тестов нет.' : 'За период результатов тестов нет.'}
                            </div>
                        ) : (
                            <div className="overflow-hidden rounded-lg border bg-card">
                                {results.map((result) => (
                                    <button
                                        key={`${result.methodCode}:${result.id}`}
                                        type="button"
                                        className="flex w-full items-start gap-3 border-b px-3 py-2 text-left last:border-0 hover:bg-accent/30"
                                        onClick={() => setEntry({ kind: 'result', result })}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-sm font-medium">{result.methodName}</span>
                                                <span className="text-[10px] text-muted-foreground">
                                                    {formatValue(result.createdAt, '', 'timestamp')}
                                                </span>
                                                {result.passed && (
                                                    <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-700 dark:text-emerald-400">
                                                        порог пройден
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-0.5 text-xs text-muted-foreground">
                                                {resultSummary(result) || 'Балл не указан'}
                                            </div>
                                            {result.conclusion && (
                                                <div className="mt-0.5 text-xs text-foreground">
                                                    {shortText(result.conclusion)}
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </section>

                    <section className="space-y-2">
                        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
                            <ClipboardList className="h-3.5 w-3.5" />
                            Назначения тестов
                        </div>
                        {assignments.length === 0 ? (
                            <div className="rounded-lg border px-3 py-2 text-xs text-muted-foreground">
                                {data.assignments.length === 0 ? 'Назначений тестов нет.' : 'За период назначений нет.'}
                            </div>
                        ) : (
                            <div className="overflow-hidden rounded-lg border bg-card">
                                {assignments.map((assignment) => {
                                    const methodName = methodsByCode.get(assignment.methodCode) || assignment.methodCode || 'Методика не указана';
                                    return (
                                        <button
                                            key={assignment.id}
                                            type="button"
                                            className="flex w-full items-start gap-3 border-b px-3 py-2 text-left last:border-0 hover:bg-accent/30"
                                            onClick={() => setEntry({ kind: 'assignment', assignment, methodName })}
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-medium">{methodName}</div>
                                                <div className="mt-0.5 text-xs text-muted-foreground">
                                                    {assignment.assignedDate
                                                        ? formatValue(assignment.assignedDate, '', 'timestamp')
                                                        : 'Дата назначения не указана'}
                                                </div>
                                            </div>
                                            <span className="shrink-0 rounded-full border bg-muted/40 px-2 py-0.5 text-[10px]">
                                                {assignmentStatusLabel(assignment.status)}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </section>

                    <section className="space-y-2">
                        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
                            <FileText className="h-3.5 w-3.5" />
                            Заключения психолога
                        </div>
                        {conclusions.length === 0 ? (
                            <div className="rounded-lg border px-3 py-2 text-xs text-muted-foreground">
                                {data.conclusions.length === 0 ? 'Заключений нет.' : 'За период заключений нет.'}
                            </div>
                        ) : (
                            <div className="overflow-hidden rounded-lg border bg-card">
                                {conclusions.map((conclusion) => (
                                    <button
                                        key={conclusion.id}
                                        type="button"
                                        className="flex w-full items-start gap-3 border-b px-3 py-2 text-left last:border-0 hover:bg-accent/30"
                                        onClick={() => setEntry({ kind: 'conclusion', conclusion })}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-sm font-medium">
                                                    {conclusion.icdCode ? `МКБ: ${conclusion.icdCode}` : 'Заключение'}
                                                </span>
                                                <span className="text-[10px] text-muted-foreground">
                                                    {formatValue(conclusion.date, '', 'date')}
                                                </span>
                                            </div>
                                            <div className="mt-0.5 text-xs text-foreground">
                                                {shortText(conclusion.text || conclusion.recommendations || 'Текст не заполнен')}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </section>
                </>
            )}

            <PatientCardPsyDetailDialog entry={entry} onClose={() => setEntry(null)} />
        </div>
    );
}
